import { createSelector } from 'reselect';
import _ from 'lodash';
import { flowTypeSelector } from '../../common/common';
import {
  offerSelector,
  extractPrice,
  roundValue,
  roundFloor,
  formatOffersForDataLayer,
  lobString
} from './offersHelpers';
import {
  addableSelector,
  tierOfServiceSelector,
  lobSelector
} from '../plan/plan';

export const filterSelector = state => _.get(state, 'offers.filterData');
export const mobileFiltersOpenSelector = state => _.get(state, 'offers.mobileFilters.open');
export const mobileFiltersHiddenSelector = state => _.get(state, 'offers.mobileFilters.hidden');
export const enableShowOffersForLobsSelector = state => _.get(state, 'offers.showOffersForLobs.isEnabled');
export const showOffersForLobsSelector = state => _.get(state, 'offers.showOffersForLobs.lobs');
export const enableDefaultLobsSelector = state => _.get(state, 'offers.enableDefaultLobs');

// HACK: Used for below showInternetOffers hack
export const enableDynamicLobSelectionSelector = state => _.get(state, 'offers.enableDynamicLobSelection');

// LOB and Option data for all checkboxes
export const checkboxFilterSelector = createSelector(
  filterSelector,
  filters => _.reject(filters, 'isSlider')
);

// All LOB and option data for selected checkboxes
export const activeCheckboxFilterSelector = createSelector(
  checkboxFilterSelector,
  filters => _.filter(filters, 'lob.selected')
);

// Just LOB data for selected checkboxes
export const activeLobFilterSelector = createSelector(
  activeCheckboxFilterSelector,
  filters => _.map(filters, 'lob')
);

// Plain array of LOBs selected
export const activelobValuesSelector = createSelector(
  activeLobFilterSelector,
  lobs => _.flatMap(lobs, 'value')
);

export const offerWithPriceSelector = createSelector(
  offerSelector,
  (offers) => _.map(offers, (offer) => ({
    ...offer,
    priceValue: extractPrice(offer.price)
  }))
);

// offers filtered by selected LOBs
// had to change equality check to xor and isEmpty because our LOB order now differs from the LOB order in the offer
export const offerLOBselector = createSelector(
  offerWithPriceSelector,
  activelobValuesSelector,
  (offers, lobs) => _.filter(offers, (offer) => _.isEmpty(_.xor(lobs, offer.linesOfBusiness)))
);

// The LOB data for all checkboxes
// Also returns them ordered by the order property
export const lobFilterSelector = createSelector(
  checkboxFilterSelector,
  filters => _.orderBy(_.map(filters, 'lob'), 'order')
);

//Add disabled and subscribed properties to lob checkbox data
export const filterWithDisabledSelector = createSelector(
  lobFilterSelector,
  addableSelector,
  tierOfServiceSelector,
  flowTypeSelector,
  enableShowOffersForLobsSelector,
  (filters, addAble, tiers, flowType, enableShowOffersForLobs) => _.map(filters, (lob) => {
    const subscribed = _.findKey(tiers, ['lineOfBusiness', lob.value]) > -1;
    const addable = _.indexOf(addAble, lob.value) > -1;
    const disabledDefault = !addable || (subscribed && !enableShowOffersForLobs); //Disabled if LOB not addable
    const disabledMoveRepackager = !addable && !subscribed; //Disabled if LOB not addable and user is not subscribed to LOB
    const disabled = flowType === 'MoveRepackager' ? disabledMoveRepackager : disabledDefault;

    return {
      ...lob,
      disabled,
      subscribed
    };
  })
);

export const showOffersForSelectedLobsSelector = createSelector(
  enableShowOffersForLobsSelector,
  showOffersForLobsSelector,
  activelobValuesSelector,
  (enabled, allDisplayableLobs, activeLobs) => {
    const noSelectedLobs = activeLobs.length === 0;
    const matchFound = enabled && _.some(allDisplayableLobs, displayableLobs => _.isEmpty(_.xor(displayableLobs, activeLobs)));

    return !enabled || noSelectedLobs || matchFound;
  }
);

export const sliderSelector = createSelector(
  filterSelector,
  filters => _.flatMap(_.filter(filters, 'isSlider'), 'options')
);

export const priceSelector = createSelector(
  offerLOBselector,
  sliderSelector,
  (offers, slider) => {
    const minMax = _.reduce(offers, (result, offer) => {
      const price = extractPrice(offer.price);

      return {
        max: price > result.max ? price : result.max,
        min: price < result.min || result.min === 0 ? price : result.min
      };
    }, {max: 0, min: 0});

    const value = slider && slider[0] ? slider[0].value : 0;
    const lowerValue = slider && slider[0] ? slider[0].lowerValue : 0;
    const computedValue = value > minMax.max || value < minMax.min ? minMax.max : value;
    const computedLowerValue = lowerValue > minMax.max || lowerValue < minMax.min ? minMax.min : lowerValue;

    if (!slider[0]) {
      return [{
        value: computedValue
      }];
    }

    //rounding so step value works properly with slider max and min
    const nearest = slider[0].step;
    const roundedMax = roundValue(minMax.max, nearest);
    const roundedMin = roundFloor(minMax.min, nearest);
    //rounding the user input because the server could return an
    //invalid value on first load and the function wouldn't be the wiser
    //HACK: find a better solution to this, because it shouldn't need to happen after first load
    const roundedComputed = roundValue(computedValue, nearest);
    const roundedComputedLower = roundFloor(computedLowerValue, nearest);

    return [
      {
        ...slider[0],
        max: roundedMax,
        min: roundedMin,
        value: roundedComputed,
        lowerValue: roundedComputedLower
      }
    ];
  }
);

export const offerPriceSelector = createSelector(
  offerLOBselector,
  priceSelector,
  (offers, price) => {
    return _.filter(offers, (offer) => {
      const offerPrice = extractPrice(offer.price);
      const value = _.get(price, '[0].value');
      const lowerValue = _.get(price, '[0].lowerValue');

      return offerPrice <= value && offerPrice >= lowerValue;
    });
  }
);

// prepares offer data for the datalayer
export const dlOffersSelector = createSelector(
  offerSelector,
  offers => formatOffersForDataLayer(offers)
);

export const summaryTitleSelector = createSelector(
  offerPriceSelector,
  activelobValuesSelector,
  (offers, lobs) => {
    const offerCount = offers.length;
    const lobStringValue = lobString(lobs);
    const summaryTitle = `${offerCount} ${lobStringValue} Offer${offers.length !== 1 ? 's' : ''}`;

    return summaryTitle;
  }
);

export const subscribedLobsSelector = createSelector(
  lobSelector,
  (lobs) => _(lobs).filter('subscribed').map('lobName').value()
);

export const subscribedLobStringSelector = createSelector(
  subscribedLobsSelector,
  (subscribedLobs) => lobString(subscribedLobs)
);

export const activeLobStringSelector = createSelector(
  activelobValuesSelector,
  lobs => lobString(lobs)
);

export const downgradeLobsSelector = createSelector(
  activelobValuesSelector,
  subscribedLobsSelector,
  (activeLobs, subscribedLobs) => _.xor(activeLobs, subscribedLobs)
);

export const downgradeLobsStringSelector = createSelector(
  downgradeLobsSelector,
  lobs => lobString(lobs)
);

export const dlPriceStringSelector = createSelector(
  priceSelector,
  (prices) => `price|${_.get(_.head(prices), 'lowerValue')}-${_.get(_.head(prices), 'value')}`
);

export const dlFilterOptionSelected = createSelector(
  dlPriceStringSelector,
  activelobValuesSelector,
  (pricesStringRaw, lobs) => {
    const priceString = `:${pricesStringRaw}`;
    const lobString = _.map(lobs, (lob) => _.lowerCase(lob)).join(':');

    return lobString + priceString;
  }
);

// HACK: determine whether or not to auto check the internet offers checkbox
// This is being developed to specifically check the internet box for a "TV ONLY" customer as they currently see 0 offers
// This should only be active when the config is turned on
// This should only return true in regular repackager flow but not for move repackage
export const showInternetOffers = createSelector(
  activelobValuesSelector,
  flowTypeSelector,
  enableDynamicLobSelectionSelector,
  (lobs, flowType, enableDynamicLobSelection) => {
    const onlyTV = _.isEqual(["TV"], lobs);
    const RepackageFlow = flowType !== 'MoveRepackager';

    return (onlyTV && RepackageFlow && enableDynamicLobSelection) ? true : false;
  }
);



/** WEBPACK FOOTER **
 ** ./client/redux/modules/data/offers/filterSelectors.js
 **/